import React, {KeyboardEvent, ReactElement} from "react";
import {throttle, debounce} from "throttle-debounce";

import {computeMenuPosition} from "/src/utils/header-nav";
import KEY from "/src/constants/keyboard-keydown-key";
import {LOCALE} from "/src/config";
import {generateKeyId, visibleForLocale} from "/src/utils/utils";
import {css} from "/src/utils/utils";
import {NAV_ITEMS_ELEM_ID} from "../navigation-items/NavigationItems";

import Icon from "../../icon/Icon";
import Image, {ImageObject} from "../../image/Image";
import LinkTag from "../../buttons/link-tag/LinkTag";

import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./navigation-item.module.scss";

type NavNodes =
  Queries.ContentfulNavComponentQueryQuery["allContentfulSuperMainNavBars"]["nodes"][0];

type NavTab = NonNullable<NavNodes["navTabs"]>[0];

interface NavigationItemProps {
  navTab: NavTab;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const THROTTLE_DELAY = 300;

const NavigationItem = (props: NavigationItemProps): ReactElement => {
  const {navTab, onMouseEnter, onMouseLeave} = props;

  const [windowWidth, setWindowWidth] = React.useState<number>(0);
  const [active, setActive] = React.useState<boolean>(false);
  const mounted = React.useRef<boolean>(false);
  const hovering = React.useRef<boolean>(false);
  const raf = React.useRef<number>(0);
  const resizeRaf = React.useRef<number>(0);
  const currentMenuItemFocus = React.useRef<HTMLElement | null>(null);
  const currentFocusMenuIndex = React.useRef<number>(-1);
  const menuBlockContainerRef = React.useRef<null | HTMLDivElement>(null);
  const menuContainerRef = React.useRef<null | HTMLDivElement>(null);
  const dropdownMenuRef = React.useRef<null | HTMLDivElement>(null);
  const menuItems = React.useRef<NodeListOf<HTMLElement> | null>(null);
  const navListItems = React.useRef<NodeListOf<HTMLElement> | null>(null);

  const resetFocusState = () => {
    setActive(false);
    currentFocusMenuIndex.current = -1;

    if (currentMenuItemFocus.current) {
      currentMenuItemFocus.current.tabIndex = -1;
      currentMenuItemFocus.current.blur();
    }

    currentMenuItemFocus.current = null;
  };

  const onResize = () => {
    if (resizeRaf.current) {
      window.cancelAnimationFrame(resizeRaf.current);
    }

    resizeRaf.current = requestAnimationFrame(() => {
      if (mounted.current) {
        // try to get document clientWidth + as window inner width value gets funky sometimes
        // and causing position jumping/flash
        const currentWidth = document.documentElement
          ? document.documentElement.clientWidth
          : window.innerWidth;

        setWindowWidth(currentWidth);
      }
    });
  };

  const debouncedResetFocusState = debounce(
    375,
    () => {
      if (hovering.current === false) {
        resetFocusState();
      }
    },
    {atBegin: false}
  );

  const throttledResize = throttle(THROTTLE_DELAY, onResize);

  const handleMouseEnter = () => {
    // let's use RAF to wait till the previous menu drop down is no longer visible
    // then show the selected menu dropdown
    // tldr; fix overlapping dropdown menu visibility
    if (navListItems.current !== null) {
      // get current expanded item index
      let index = -1;

      for (let i = 0; i < navListItems.current.length; i++) {
        if (navListItems.current[i].getAttribute("aria-expanded") === "true") {
          index = i;
          break;
        }
      }

      if (index === -1) {
        setActive(true);

        if (raf.current) {
          window.cancelAnimationFrame(raf.current);
        }
      } else {
        raf.current = window.requestAnimationFrame(handleMouseEnter);
      }
    }
  };

  const onMenuMouseEnter = () => {
    hovering.current = true;

    if (onMouseEnter) {
      onMouseEnter();
    }

    handleMouseEnter();
  };

  const onMenuMouseLeave = () => {
    if (raf.current) {
      window.cancelAnimationFrame(raf.current);
    }

    hovering.current = false;

    debouncedResetFocusState();

    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  // We want to position the dropdown with the menu caret centered container
  const computePosition = () => {
    // set position of the menu wrapper, to make sure that it's not overflowing
    if (menuContainerRef.current && dropdownMenuRef.current) {
      return computeMenuPosition(
        windowWidth,
        menuContainerRef.current,
        dropdownMenuRef.current
      );
    }

    return {
      left: 0,
      right: 0,
    };
  };

  const focusNavItemByIndex = (
    idx: number,
    isKeyboardEvent: boolean = false
  ) => {
    if (!menuItems.current) {
      return;
    }

    // focus to last nav
    const focusMenuItem = menuItems.current[idx] as HTMLElement;

    if (currentMenuItemFocus.current) {
      currentMenuItemFocus.current.blur();
    }

    if (isKeyboardEvent) {
      focusMenuItem.focus();
    }

    currentFocusMenuIndex.current = idx;
    currentMenuItemFocus.current = focusMenuItem;
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!menuItems.current || currentFocusMenuIndex.current === null) {
      return;
    }

    const lastMenuIdx = menuItems.current.length - 1;

    switch (e.key) {
      case KEY.ENTER:
      case KEY.SPACE:
      case KEY.DOWN:
      case KEY.UP:
        {
          // we don't want event to bubble up/down
          if (e.key !== KEY.ENTER) {
            e.preventDefault();
          }

          // for enter event
          // if already opened, do nothing
          if (e.key === KEY.ENTER && active) {
            return;
          }

          let focusIndex = 0;
          if (e.key === KEY.DOWN) {
            focusIndex =
              currentFocusMenuIndex.current === lastMenuIdx
                ? 0
                : currentFocusMenuIndex.current + 1;
          } else if (e.key === KEY.UP) {
            focusIndex =
              currentFocusMenuIndex.current <= 0
                ? lastMenuIdx
                : currentFocusMenuIndex.current - 1;
          }

          if (focusIndex !== -1) {
            focusNavItemByIndex(focusIndex, true);
          }

          // space, enter, down should trigger the dropdown
          setActive(true);
        }
        break;

      case KEY.END:
      case KEY.DOWN:
      case KEY.UP:
      case KEY.HOME:
      case KEY.RIGHT:
      case KEY.LEFT:
      case KEY.ESC:
      case KEY.TAB:
        resetFocusState();
        break;

      default:
        break;
    }
  };

  React.useEffect(() => {
    if (!mounted.current) {
      onResize();
      window.addEventListener("resize", throttledResize);
      mounted.current = true;
    }

    if (menuBlockContainerRef.current) {
      menuItems.current =
        menuBlockContainerRef.current.querySelectorAll("[role=menuitem]");
    }

    if (!navListItems.current) {
      const temp = document.getElementById(NAV_ITEMS_ELEM_ID);
      if (temp) {
        navListItems.current = temp.querySelectorAll("div[role='menuitem']");
      }
    }

    return () => {
      window.removeEventListener("resize", throttledResize);

      mounted.current = false;
    };
  });

  const computedMenuPos = computePosition();

  // there are upto 4 columns, we are just going to hard code this
  const colItems = [
    {
      title: navTab?.column1Title,
      items: navTab?.column1SubItems,
    },
    {
      title: navTab?.column2Title,
      items: navTab?.column2SubItems,
    },
    {
      title: navTab?.column3Title,
      items: navTab?.column3SubItems,
    },
    {
      title: navTab?.column4Title,
      items: navTab?.column4SubItems,
    },
  ];

  if (navTab && !!navTab.url === true) {
    return (
      <LinkTag
        tabIndex={-1}
        role="menuitem"
        classes={css("p-small", styles.navigationItem__linkItem)}
        href={navTab.url!}
      >
        <span>{navTab.tabTitle}</span>
      </LinkTag>
    );
  }

  return (
    <div
      onMouseEnter={onMenuMouseEnter}
      onMouseLeave={onMenuMouseLeave}
      ref={dropdownMenuRef}
      className={css(
        styles.navigationItem,
        active ? styles.navigationItemActive : ""
      )}
    >
      {/* load caret */}
      {active && <div className={styles.navigationItem__caret} />}

      <div
        aria-haspopup="true"
        aria-expanded={active}
        onKeyDown={handleOnKeyDown}
        tabIndex={-1}
        role="menuitem"
        className={css("p-small", styles.navigationItem__menuItem)}
      >
        <span>{navTab?.tabTitle}</span>
        <Icon
          svg={ChevronRightIcon}
          classes={styles.navigationItem__menuItem__icon}
        />
      </div>
      <div
        role="none"
        tabIndex={-1}
        onKeyDown={handleOnKeyDown}
        className={styles.navigationItem__dropdown}
        ref={menuContainerRef}
        style={computedMenuPos}
      >
        <div
          className={styles.navigationItem__dropdown__itemsWrapper}
          ref={menuBlockContainerRef}
        >
          {colItems.map((item, colIndex) => {
            const colSubItems = (item.items || []).filter(item => {
              const visibleIn: Array<string> =
                item && item.visibleIn ? (item.visibleIn as Array<string>) : [];

              return visibleForLocale(visibleIn, LOCALE);
            });
            const colTitle = item.title;

            if (colSubItems.length === 0) {
              return null;
            }

            return (
              <div
                key={generateKeyId(`menu-nav-col-items-${colIndex}`)}
                className={styles.navigationItem__dropdown__col}
              >
                {colTitle && (
                  <h2
                    className={css(
                      styles.navigationItem__dropdown__listTitle,
                      "h5"
                    )}
                  >
                    {colTitle}
                  </h2>
                )}
                <ul
                  role="menu"
                  className={styles.navigationItem__dropdown__list}
                >
                  {colSubItems.map((item: any, i: number) => {
                    return (
                      <li
                        role="none"
                        className={css(styles.navigationItem__dropdown__item)}
                        key={generateKeyId(
                          `nav-subitems-1-${item?.menuItemTitle}-${i}`
                        )}
                      >
                        <LinkTag
                          classes={css(
                            "h5",
                            styles.navigationItem__dropdown__link
                          )}
                          tabIndex={-1}
                          role="menuitem"
                          href={item?.menuItemLink!}
                        >
                          <div
                            className={
                              styles.navigationItem__dropdown__linkContent
                            }
                          >
                            {item?.menuItemIcon != null && (
                              <Image
                                image={item.menuItemIcon as ImageObject}
                                classes={
                                  styles.navigationItem__dropdown__linkIcon
                                }
                              />
                            )}
                            <div
                              className={css(
                                styles.navigationItem__dropdown__linkCopy
                              )}
                            >
                              <span
                                className={
                                  styles.navigationItem__dropdown__linkText
                                }
                              >
                                {item?.menuItemTitle}
                              </span>
                              <span
                                className={css(
                                  "caption",
                                  styles.navigationItem__dropdown__linkDesc
                                )}
                              >
                                {item?.menuItemDescription}
                              </span>
                            </div>
                          </div>
                        </LinkTag>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NavigationItem;
